import { Info } from 'lucide-react'
import type React from 'react'
import { useTranslation } from 'react-i18next'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'

interface MetaFieldProps {
  label: string
  value: string
  sub?: string
  info?: React.ReactNode
}

/** The small upper-case caption of a detail field. */
export function MetaFieldLabel({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 10,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        color: 'var(--muted-foreground)',
        fontWeight: 600,
        marginBottom: 6,
      }}
    >
      {children}
    </div>
  )
}

/** The line under the caption: the value and what sits beside it. */
export function MetaFieldValueRow({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 13.5,
        fontWeight: 500,
        color: 'var(--foreground)',
      }}
    >
      {children}
    </div>
  )
}

export function MetaField({ label, value, sub, info }: MetaFieldProps) {
  const { t } = useTranslation()
  return (
    <div>
      <MetaFieldLabel>{label}</MetaFieldLabel>
      <MetaFieldValueRow>
        <span className="max-md:min-w-0 max-md:wrap-anywhere">{value}</span>
        {info && (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={t('documents.detail.fieldDetails', { label })}
                // A ~33px tap target around the 13px icon; the negative margin cancels
                // the padding, so the row keeps its layout.
                className="p-0 max-md:-m-2.5 max-md:p-2.5"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  color: 'var(--muted-foreground)',
                }}
              >
                <Info size={13} />
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              className="w-80 text-sm max-md:max-h-(--radix-popover-content-available-height) max-md:overflow-y-auto"
            >
              {info}
            </PopoverContent>
          </Popover>
        )}
      </MetaFieldValueRow>
      {sub && (
        <div
          style={{
            fontSize: 11,
            color: 'var(--muted-foreground)',
            fontFamily: 'var(--font-mono)',
            marginTop: 3,
          }}
        >
          {sub}
        </div>
      )}
    </div>
  )
}
